import BreadcrumbItem from 'Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Grid from 'Components/Atoms/Grid/Grid';
import Breadcrumbs from 'Components/Molecules/Breadcrumbs/Breadcrumbs';
import PageHeader from 'Components/Organisms/PageHeader/PageHeader';
import React, {Suspense, useEffect, useMemo, useState} from 'react';
import {ROUTES} from 'Services/Route/Route.types';
import styles from './OptionsOverview.module.sass';
import StatusLabel from 'Components/Atoms/Label/StatusLabel/StatusLabel';
import OverviewHeader from 'Components/Molecules/Headers/OverviewHeader/OverviewHeader';
import Container from 'Components/Atoms/Container/Container';
import {TableProvider} from 'Services/Table/TableContext';
import TextField from 'Components/Atoms/Fields/TextField/TextField';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import {faMagnifyingGlass} from '@fortawesome/pro-light-svg-icons';
import Dropdown from 'Components/Atoms/Dropdown/Dropdown';
import Button from 'Components/Atoms/Button/Button';
import {faFilter} from '@fortawesome/pro-regular-svg-icons';
import Loader from 'Components/Atoms/Loader/Loader';
import TableFixed from 'Components/Molecules/Table/TableFixed/TableFixed';
import TableHeader from 'Components/Molecules/TableHeader/TableHeader';
import TableColumn from 'Components/Molecules/TableColumn/TableColumn';
import TableBody from 'Components/Molecules/TableBody/TableBody';
import useFetch from 'Api/useFetch';
import TableRow from 'Components/Molecules/TableRow/TableRow';
import TableCell from 'Components/Molecules/TableCell/TableCell';
import TableLoader from 'Components/Atoms/Loader/TableLoader/TableLoader';
import { useNavigate } from 'react-router-dom';
import {optionListStatusColorMap, optionListStatusMap} from 'Components/Organisms/OptionList/Options';
import OptionListFilters from 'Components/Organisms/OptionList/OptionListFilters/OptionListFilters';
import ActiveFilterBlocks from 'Components/Molecules/Filters/ActiveFilterBlocks/ActiveFilterBlocks';
import StatusList from 'Components/Molecules/StatusList/StatusList';
import Text from 'Components/Atoms/Text/Text';

const OptionsOverview = () => {
    const pageSize = 20;
    const navigate = useNavigate();
    const [filters, setFilters] = useState<any>({});
    const [searchTimer, setSearchTimer] = useState<any>(null);
    const [metadata, setMetadata] = useState({
        total: 0,
        total_filtered: 0,
        status_concept: 0,
        status_agreed: 0,
        status_send: 0,
        status_proposed: 0,
        status_rejected: 0,
    });
    const [state, setState] = useState({
        searchQuery: '',
        currentPage: 1
    });

    useEffect(() => {
        console.log(filters);
    }, [filters]);

    const activeFilterKeys = useMemo(() =>
            Object.keys(filters)
                ?.filter(i => !['query', 'type'].includes(i))
        , [filters]);

    const requestParams = useMemo<any>(() => {
        let params: any = {
            limit: pageSize,
            offset: 0,
            "order[sent_at]": 'DESC',
        }

        if (state.currentPage !== 0) params.offset = (state.currentPage - 1) * pageSize;
        if (filters) {
            const filterRequest = {};
            for (const key in filters) {
                const newKey = `filters[${key}]`;
                filterRequest[newKey] = filters[key];
            }

            params = {...params, ...filterRequest};
        };

        return params;
    }, [state.currentPage, filters]);

    const {
        isLoading,
        isError,
        data,
        isFetching,
        refetch
    } = useFetch(
        '/api/v2.9/candidates/options',
        requestParams,
        {
            select: (data) => data?.data,
            onSuccess: (data) => {
                setState((state) => ({
                    ...state,
                    isPageLoading: false
                }));
                setMetadata(data?.metadata);
            }
        }
    );

    const handleSearchQuery = (value: string) => {
        clearTimeout(searchTimer);
        setSearchTimer(null);

        setState((state) => ({
            ...state,
            searchQuery: value
        }));

        setSearchTimer(
            setTimeout(() => {
                let newFilters = { ...filters };
                if (value) {
                    newFilters = {
                        ...newFilters,
                        query: value
                    };
                } else {
                    delete newFilters['query'];

                    newFilters = { ...newFilters };
                }

                setFilters(newFilters);
            }, 500)
        );
    };

    const handleRemoveFilter = (i: any) => {
        const newFilters = {...filters};
        delete newFilters[i];

        setFilters(newFilters);
    }

    const handleRowClick = (candidateId: any, option_list_id: any) => () => navigate(`${ROUTES.CANDIDATES.DETAILED.getFullPath(candidateId)}?tab=shortlists&option_list_id=${option_list_id}`);

    return <Grid gap={'none'}>
        <PageHeader
            breadcrumbs={
                <Breadcrumbs>
                    <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
                       <BreadcrumbItem size='small'>Shortlists</BreadcrumbItem>
                </Breadcrumbs>
            }
        >
            <div className={styles['header']}>
                <OverviewHeader
                    icon={'options'}
                    title={`${ metadata?.total || '-' } Shortlists`}
                >
                    <Dropdown
                        triggerAction='hover'
                        trigger={
                            <StatusLabel status='active'>
                                { metadata?.status_proposed + metadata?.status_agreed } Actief
                            </StatusLabel>
                        }
                    >
                        <Suspense fallback={<Loader />}>
                            <StatusList
                                list={[
                                    {
                                        status: 'ACTIVE',
                                        status_substatus: 'Akkoord',
                                        total: metadata?.status_agreed ?? 0
                                    },
                                    {
                                        status: 'ACTIVE',
                                        status_substatus: 'Aangeboden',
                                        total: metadata?.status_proposed ?? 0
                                    }
                                ]}
                            />
                        </Suspense>
                    </Dropdown>
                    <Dropdown
                        triggerAction='hover'
                        trigger={
                            <StatusLabel status='passive'>
                                { metadata?.status_concept + metadata?.status_send } Passief
                            </StatusLabel>
                        }
                    >
                        <Suspense fallback={<Loader />}>
                            <StatusList
                                list={[
                                    {
                                        status: 'PASSIVE',
                                        status_substatus: 'Concept',
                                        total: metadata?.status_concept ?? 0
                                    },
                                    {
                                        status: 'PASSIVE',
                                        status_substatus: 'Verzonden',
                                        total: metadata?.status_send ?? 0
                                    }
                                ]}
                            />
                        </Suspense>
                    </Dropdown>
                    <Dropdown
                        triggerAction='hover'
                        trigger={
                            <StatusLabel status='inactive'>
                                { metadata?.status_rejected } Niet actief
                            </StatusLabel>
                        }
                    >
                        <Suspense fallback={<Loader />}>
                            <StatusList
                                list={[
                                    {
                                        status: 'INACTIVE',
                                        status_substatus: 'Niet akkoord',
                                        total: metadata?.status_rejected ?? 0
                                    }
                                ]}
                            />
                        </Suspense>
                    </Dropdown>
                </OverviewHeader>
            </div>
        </PageHeader>

        <Container padding={'vertical'}>
            <TableProvider
                pageNumber={1}
                totalPageNumber={1}
                onPageChange={() => {}}
            >
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        maxWidth: '550px'
                    }}>
                        <TextField
                            iconBefore={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                            size='large'
                            placeholder={`Zoeken op bedrijfsnaam of url...`}
                            disableBlur

                            value={state.searchQuery}
                            onChange={handleSearchQuery}
                        />
                        <Dropdown
                            disableCloseOnOutsideClick
                            disableCloseOnClick
                            triggerAction='click'
                            trigger={
                                <Button
                                    variant='transparent'
                                    color='primary'
                                    size='large'
                                    iconBefore={<FontAwesomeIcon icon={faFilter} />}
                                >
                                    Filters beheren
                                </Button>
                            }
                        >
                            <Suspense
                                fallback={
                                    <Loader />
                                }
                            >
                                <OptionListFilters
                                    filters={filters}
                                    onFilterChange={setFilters}
                                />
                            </Suspense>
                        </Dropdown>
                    </div>

                    <ActiveFilterBlocks
                        statusMapper={optionListStatusMap}
                        metadata={metadata}
                        filters={filters}
                        onDelete={handleRemoveFilter}
                    />

                    <TableFixed
                        header={
                            <TableHeader>
                                <TableColumn
                                    position='fixed'
                                    id='status'
                                >
                                    Status
                                </TableColumn>
                                <TableColumn
                                    position='fixed'
                                    size='large'
                                    id='name'
                                    primary
                                >
                                    Kandidaat
                                </TableColumn>
                                <TableColumn size='large'>
                                    Lijst
                                </TableColumn>
                                <TableColumn size='large'>
                                    Bedrijfsnaam
                                </TableColumn>
                                <TableColumn size='large'>
                                    Vacature URL
                                </TableColumn>
                                <TableColumn size='large'>
                                    Opmerking
                                </TableColumn>
                                <TableColumn>
                                    Verzonden door
                                </TableColumn>
                                <TableColumn>
                                    Verzonden op
                                </TableColumn>
                                <TableColumn>
                                    Aangemaakt door
                                </TableColumn>
                                <TableColumn>
                                    Aangemaakt op
                                </TableColumn>
                            </TableHeader>
                        }
                    >
                        <TableBody>
                            {
                                isLoading
                                    ? <TableLoader />
                                    : data?.options?.map(i => <TableRow onClick={handleRowClick(i?.candidate?.id, i?.candidate_option_list_id)}>
                                        <TableCell
                                            position='fixed'
                                        >
                                            <StatusLabel status={optionListStatusColorMap?.[i?.status]}>
                                                {optionListStatusMap?.[i?.status] || '-'}
                                            </StatusLabel>
                                        </TableCell>
                                        <TableCell
                                            position='fixed'
                                            size='large'
                                            offsetLeft={200}
                                            primary
                                        >
                                            <Text>
                                                { i?.candidate?.display_name || '-' }
                                            </Text>
                                        </TableCell>
                                        <TableCell size='large'>
                                            <Text>
                                                { i?.candidate_option_list?.name || '-' }
                                            </Text>
                                        </TableCell>
                                        <TableCell size='large'>
                                            <Text>
                                                { i?.company_name || '-' }
                                            </Text>
                                        </TableCell>
                                        <TableCell size={'large'}>
                                            <Text tooltip={i?.url}>
                                                <a className={styles['link']} href={i?.url?.startsWith('https://') ? i?.url : `https://${i?.url}`} target={'_blank'} onClick={(e) => e.stopPropagation()}>
                                                    {i?.url || '-'}
                                                </a>
                                            </Text>
                                        </TableCell>
                                        <TableCell size={'large'}>
                                            <Text>
                                                { i?.description || '-' }
                                            </Text>
                                        </TableCell>
                                        <TableCell>
                                            { i?.sent_by || '-' }
                                        </TableCell>
                                        <TableCell>
                                            { i?.sent_at || '-' }
                                        </TableCell>
                                        <TableCell>
                                            { i?.created_by || '-' }
                                        </TableCell>
                                        <TableCell>
                                            { i?.created_at || '-' }
                                        </TableCell>
                                    </TableRow>)
                            }
                        </TableBody>
                    </TableFixed>
                </div>
            </TableProvider>
        </Container>
    </Grid>;
}

export default OptionsOverview;